import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { useList, useMutate } from 'src/hooks/useFetch.hook';
import { useSelector } from 'react-redux';
import { sendPromoCodeMobileCustomerUrl } from 'src/react-query/endPoints';

const SendPromoModal = ({ open, onClose, selectedCustomers = [] }) => {
  const theme = useTheme();
  const brandGradient = theme.palette.gradients.brand;
  const brandHoverGradient = theme.palette.gradients.brandHover;
  const focusBorder = theme.palette.primary.main;
  const hoverSurface = alpha(theme.palette.primary.main, 0.08);
  const { teamId } = useSelector((state) => state.teams);
  const [selectedPromoCode, setSelectedPromoCode] = useState('');
  const [communicationChannels, setCommunicationChannels] = useState({
    email: true,
    sms: false,
    whatsapp: false
  });
  const [customMessage, setCustomMessage] = useState('');
  const { mutate, isLoading } = useMutate("sendPromoCodeMobileCustomers", teamId);
  const { data, isFetching } = useList("promoCodes");

  const handleChannelChange = (channel) => {
    setCommunicationChannels(prev => ({
      ...prev,
      [channel]: !prev[channel]
    }));
  };

  const handleSend = () => {
    //filter and send the customer Id only
    const selectedCustomerIDs = selectedCustomers.map(customer => customer.id);

    mutate(
      {
        url: sendPromoCodeMobileCustomerUrl(teamId),
        method: 'PUT',
        body: { 
          customers: selectedCustomerIDs,
          promocodeId: selectedPromoCode,
          channels: communicationChannels,
        },
      },
      {
        onSuccess: () => {
          handleCancel();
        }
      }
    );

    // Close modal after sending
    onClose();
    
    // You can add success notification here
  };

  const handleCancel = () => {
    // Reset form
    setSelectedPromoCode('');
    setCommunicationChannels({ email: true, sms: false, whatsapp: false });
    setCustomMessage('');
    onClose();
  };

  const getEnabledChannelsCount = () => {
    return Object.values(communicationChannels).filter(Boolean).length;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon icon="eva:paper-plane-fill" width={24} height={24} />
          <Typography variant="h6" component="span">
            Send Promo Code to Customers
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Icon icon="eva:close-fill" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Selected Customers */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Selected Customers ({selectedCustomers.length})
          </Typography>
          <Box
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              borderRadius: 1,
              p: 2,
              maxHeight: 120,
              overflowY: 'auto',
              backgroundColor: theme.palette.background.paper
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedCustomers.map((customer, index) => (
                <Chip
                  key={customer.id || index}
                  label={customer.email || customer.fullName || `Customer ${index + 1}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Select Promo Code */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Select Promo Code
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedPromoCode}
              onChange={(e) => setSelectedPromoCode(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: focusBorder,
                    boxShadow: `0 0 0 3px ${alpha(focusBorder, 0.15)}`
                  }
                }
              }}
            >
              <MenuItem value="" disabled>
                <Typography color="textSecondary">Choose a promo code</Typography>
              </MenuItem>
              {data && data.map((promo) => (
                <MenuItem key={promo.id} value={promo.id}>
                  {promo?.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Important Notice */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            <strong>Important:</strong> Promo codes will only be sent to customers who have the relevant contact
            information (email, phone) in our database. SMS delivery may have geographic restrictions 
            - for example, customers in India require DLT template compliance. Only available 
            communication channels will be used for each customer.
          </Typography>
        </Alert>

        {/* Communication Channels */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Communication Channels
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={communicationChannels.email}
                  onChange={() => handleChannelChange('email')}
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.55),
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon="eva:email-fill" width={20} height={20} />
                  <Typography>Email</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={communicationChannels.sms}
                  onChange={() => handleChannelChange('sms')}
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.55),
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon="eva:message-square-fill" width={20} height={20} />
                  <Typography>SMS</Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={communicationChannels.whatsapp}
                  onChange={() => handleChannelChange('whatsapp')}
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.55),
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon="eva:phone-fill" width={20} height={20} />
                  <Typography>WhatsApp</Typography>
                </Box>
              }
            />
          </Box>
        </Box>

        {/* Custom Message */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Custom Message (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add a personal message to accompany the promo code..."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: focusBorder,
                  boxShadow: `0 0 0 3px ${alpha(focusBorder, 0.15)}`
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            color: theme.palette.text.secondary,
            borderColor: theme.palette.divider,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: hoverSurface
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={!selectedPromoCode || getEnabledChannelsCount() === 0}
          startIcon={<Icon icon="eva:paper-plane-fill" />}
          sx={{
            backgroundImage: brandGradient,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundImage: brandHoverGradient
            },
            '&:disabled': {
              backgroundImage: 'none',
              backgroundColor: alpha(theme.palette.text.disabled, 0.2)
            }
          }}
        >
          Send to {selectedCustomers.length} Customer{selectedCustomers.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendPromoModal;
